/**
 * vz: require an explicit size choice on the product page.
 *
 * Shopify always resolves a product to its first available variant, so on a
 * product with both "Size Top" and "Size Bottom" a shopper could add to cart
 * having only noticed the top — the bottom silently kept its default. The picker
 * snippets therefore no longer emit `checked` for size options; they mark the
 * group with data-vz-require-choice and keep Shopify's resolved value in
 * data-vz-default-checked. This script keeps that state honest across section
 * re-renders and validates on submit.
 *
 * Add to cart keeps its normal look at all times — including through the section
 * fetch that follows every option change, which product-info.js would otherwise
 * spend disabling it. Clicking it with a size still unchosen shakes the button and
 * shows the line under the size row that's missing.
 *
 * Colour/swatch options are untouched — price and media need a resolved variant.
 */
(() => {
  const GROUP = '[data-vz-require-choice]';
  const MESSAGE = 'vz-size-error';

  // section id -> Set of option names the shopper has actually chosen
  const chosenBySection = new Map();

  const chosenSet = (productInfo) => {
    const key = productInfo.dataset.section || productInfo.id || 'default';
    if (!chosenBySection.has(key)) chosenBySection.set(key, new Set());
    return chosenBySection.get(key);
  };

  // During a re-render the outgoing <variant-selects> lingers (hidden) for 500ms,
  // so only read the first one — that is the freshly inserted node.
  const groups = (productInfo) => {
    const picker = productInfo.querySelector('variant-selects');
    return picker ? Array.from(picker.querySelectorAll(GROUP)) : [];
  };

  const missingGroups = (productInfo) =>
    groups(productInfo).filter((group) => !group.querySelector('input[type="radio"]:checked'));

  const submitButton = (productInfo) => productInfo.querySelector('.product-form__submit');
  const addToCartForm = (productInfo) => productInfo.querySelector('form[data-type="add-to-cart-form"]');

  /* product-info.js disables Add to cart for the length of the section fetch that
     follows every option change. Fading out and back in on each size tap reads as
     broken, so the button stays live — and a click landing mid-fetch is held, then
     replayed once the new variant is in, rather than adding the previous one. */
  const PENDING_TIMEOUT = 6000;
  const pendingSince = new WeakMap();
  const wasLive = new WeakMap();

  const isPending = (productInfo) => {
    const since = pendingSince.get(productInfo);
    return !!since && Date.now() - since < PENDING_TIMEOUT;
  };

  const spinner = (productInfo, show) => {
    const button = submitButton(productInfo);
    if (!button) return;
    button.classList.toggle('loading', show);
    button.querySelector('.loading__spinner')?.classList.toggle('hidden', !show);
  };

  const replayHeldSubmit = (productInfo) => {
    if (productInfo.dataset.vzHeldSubmit !== 'true') return;
    delete productInfo.dataset.vzHeldSubmit;

    const button = submitButton(productInfo);
    // Sold out / unavailable, or a size still outstanding: drop the held click.
    if (!button || button.hasAttribute('disabled') || missingGroups(productInfo).length) return;

    const form = addToCartForm(productInfo);
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(button);
    else button.click();
  };

  const prompt = (group) => {
    const name = group.dataset.vzOptionName || 'size';
    return `Please select ${name.toLowerCase() === 'size' ? 'a size' : name}`;
  };

  const clearMessage = (group) => {
    group.removeAttribute('data-vz-missing');
    group.querySelector(`.${MESSAGE}`)?.remove();
  };

  const showMessages = (productInfo, missing, { shake = true } = {}) => {
    groups(productInfo).forEach(clearMessage);

    missing.forEach((group) => {
      group.setAttribute('data-vz-missing', '');
      const message = document.createElement('p');
      message.className = MESSAGE;
      message.setAttribute('role', 'alert');
      message.textContent = prompt(group);
      group.appendChild(message);
    });

    if (!shake) return;

    const button = productInfo.querySelector('.product-form__submit');
    if (button) {
      button.classList.remove('vz-shake');
      // Reflow, so a second click restarts the animation rather than doing nothing.
      void button.offsetWidth;
      button.classList.add('vz-shake');
    }

    const first = missing[0];
    first.querySelector('input[type="radio"]:not(.disabled)')?.focus({ preventScroll: true });
    const { top, bottom } = first.getBoundingClientRect();
    if (top < 0 || bottom > window.innerHeight) {
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // An unresolved size means there is no variant to add, so keep the form's id
  // empty: if this script ever fails to block a submit, the shopper gets an error
  // rather than someone else's bottom size. product-info.js fills it back in on
  // the re-render that follows a complete selection.
  const syncVariantInput = (productInfo) => {
    if (!groups(productInfo).length) return;
    if (!missingGroups(productInfo).length) return;
    const idInput = addToCartForm(productInfo)?.querySelector('input[name="id"]');
    if (idInput) idInput.value = '';
  };

  // Runs on fetched markup before it is inserted, so a size the shopper already
  // picked never flickers off. viewTransition re-serialises the node, so the
  // attribute — not the property — is what survives.
  const restoreChecked = (chosen) => (newContent) => {
    if (!newContent?.querySelectorAll) return;
    newContent.querySelectorAll(GROUP).forEach((group) => {
      if (!chosen.has(group.dataset.vzOptionName)) return;
      group.querySelector('input[data-vz-default-checked]')?.setAttribute('checked', 'checked');
    });
  };

  const init = (productInfo) => {
    if (!productInfo || productInfo.dataset.vzRequireSize === 'ready') return;
    const optionGroups = groups(productInfo);
    if (!optionGroups.length) return;
    productInfo.dataset.vzRequireSize = 'ready';

    const chosen = chosenSet(productInfo);

    // A ?variant= link names a variant on purpose (shared link, reload after
    // picking), so honour it as a real choice rather than a Shopify default.
    if (new URLSearchParams(window.location.search).has('variant')) {
      optionGroups.forEach((group) => {
        chosen.add(group.dataset.vzOptionName);
        const input = group.querySelector('input[data-vz-default-checked]');
        if (input) input.checked = true;
      });
    }

    productInfo.addPreProcessCallback?.(restoreChecked(chosen));
    syncVariantInput(productInfo);
  };

  const sweep = () => document.querySelectorAll('product-info').forEach(init);

  document.addEventListener('product-info:loaded', (event) => init(event.target));

  const pickerProductInfo = (event) => {
    const picker = event.target?.closest?.('variant-selects');
    return picker ? picker.closest('product-info') : null;
  };

  // Capture phase: records the choice — and the button's real state — before
  // <variant-selects> fires the fetch for the new option values and product-info.js
  // disables the button for its duration.
  document.addEventListener(
    'change',
    (event) => {
      const productInfo = pickerProductInfo(event);
      if (!productInfo) return;

      const group = event.target.closest(GROUP);
      if (group) {
        init(productInfo);
        chosenSet(productInfo).add(group.dataset.vzOptionName);
        clearMessage(group);
      }

      const button = submitButton(productInfo);
      wasLive.set(productInfo, !!button && !button.hasAttribute('disabled'));
      pendingSince.set(productInfo, Date.now());
    },
    true
  );

  // Bubble phase: runs after product-info.js has disabled the button, so this is
  // where it goes back to looking normal. Only if it was live to begin with — a
  // sold-out button is left alone.
  document.addEventListener('change', (event) => {
    const productInfo = pickerProductInfo(event);
    if (!productInfo || !wasLive.get(productInfo)) return;
    submitButton(productInfo)?.removeAttribute('disabled');
  });

  // Capture phase again: stops product-form.js from ever seeing the submit.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form?.matches?.('form[data-type="add-to-cart-form"]')) return;
      const productInfo = form.closest('product-info');
      if (!productInfo) return;

      const missing = missingGroups(productInfo);
      if (missing.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        productInfo.dataset.vzValidated = 'true';
        showMessages(productInfo, missing);
        return;
      }

      // Mid-fetch: the form still holds the previous variant, so hold the click
      // and replay it on the re-render. Dawn's own spinner covers the wait.
      if (isPending(productInfo)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        productInfo.dataset.vzHeldSubmit = 'true';
        spinner(productInfo, true);
        setTimeout(() => {
          if (productInfo.dataset.vzHeldSubmit !== 'true') return;
          delete productInfo.dataset.vzHeldSubmit;
          spinner(productInfo, false);
        }, PENDING_TIMEOUT);
      }
    },
    true
  );

  // Fired at the end of every product-info re-render. The markup is new, so any
  // message is gone: put it back for whatever is still unchosen, without shaking.
  document.addEventListener('variant:change', () => {
    sweep();
    document.querySelectorAll('product-info').forEach((productInfo) => {
      pendingSince.delete(productInfo);
      syncVariantInput(productInfo);

      if (productInfo.dataset.vzValidated === 'true') {
        const missing = missingGroups(productInfo);
        if (missing.length) showMessages(productInfo, missing, { shake: false });
        else delete productInfo.dataset.vzValidated;
      }

      spinner(productInfo, false);
      replayHeldSubmit(productInfo);
    });
  });

  sweep();
  document.addEventListener('DOMContentLoaded', sweep);
})();
